import React, { useState, useContext } from 'react';
import AppNavbar from '../Components/AppNavbar';
import { BackendContext } from '../Context/BackendContext';
import CheckoutForm from '../Components/CheckoutForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  Button,
  RadioGroup,
  Radio,
  cn,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tabs,
  Tab,
} from "@heroui/react";
import { Gem, Clock, UserPlus, ShoppingCart, Zap } from "lucide-react";
import { ChromaIcon } from '../Components/Icons/ChromaIcon.tsx';
import LogInRequiredModal from '../Components/LogInRequiredModal';
import { CongratsModal } from '../Components/CongratsModal.js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const appearance = {
  theme: 'night',
  variables: {
    fontFamily: 'Sohne, system-ui, sans-serif',
    fontWeightNormal: '500',
    borderRadius: '8px',
    colorBackground: '#0A2540',
    colorPrimary: '#4d3466ff',
    accessibleColorOnColorPrimary: '#1A1B25',
    colorText: 'white',
    colorTextSecondary: 'white',
    colorTextPlaceholder: '#ABB2BF',
    tabIconColor: 'white',
    logoColor: 'dark',
  },
  rules: {
    '.Input': {
      backgroundColor: '#212D63',
      border: '1px solid var(--indigo)',
    },
  },
};

// ---- Shared small pieces ----
export const CustomRadio = (props) => {
  const { children, ...otherProps } = props;
  return (
    <Radio
      {...otherProps}
      classNames={{
        base: cn(
          "group relative m-0 h-full w-full max-w-none cursor-pointer rounded-2xl",
          "border-0 bg-transparent p-0 shadow-none"
        ),
        wrapper: "absolute left-4 top-4 z-10 border-blue-500 bg-slate-950/70 text-blue-500 data-[selected=true]:border-blue-500 data-[selected=true]:text-blue-500 after:bg-blue-500",
      }}
    >
      {children}
    </Radio>
  );
};

const formatInt = n => Number(n ?? 0).toLocaleString('en-US');

// Example store packages (unchanged)
const packages = [
  { id: 0, title: 'Buy me a Burger', tokens: 3000,  extraTokens: 200,   price: '$4.99',  image: '/images/starter.png'  },
  { id: 1, title: 'Make it a Meal', tokens: 9000,  extraTokens: 1000,  price: '$14.99', image: '/images/builder.png'  },
  { id: 2, title: 'Add Curly Fries',  tokens: 20000, extraTokens: 3000,  price: '$29.99', image: '/artistPack.png'      },
  { id: 3, title: 'No Slaw, Extra Piece of Toast',    tokens: 40000, extraTokens: 6000,  price: '$49.99', image: '/images/mega.png'     },
  { id: 4, title: "With a Double Protein Milkshake",  tokens: 70000, extraTokens: 10000, price: '$74.99', image: '/images/stuDio.png'    },
  { id: 5, title: 'Put a Ribeye on It',tokens: 100000,extraTokens: 15000, price: '$99.99', image: '/images/universe.png'  },
];

const PACK_THEMES = {
  0: {
    wrap: "border-slate-700/80 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_72%)]",
    icon: "border-slate-500/30 bg-slate-400/10",
    bonus: "text-slate-200",
    button: "bg-slate-700 border-slate-500 hover:bg-slate-600",
    badge: "Starter",
    badgeClass: "border-slate-500/30 bg-slate-400/10 text-slate-100",
  },
  1: {
    wrap: "border-indigo-700/70 bg-[radial-gradient(circle_at_top,_rgba(79,70,229,0.24),_transparent_72%)]",
    icon: "border-indigo-400/30 bg-indigo-500/12",
    bonus: "text-indigo-300",
    button: "bg-indigo-600 border-indigo-800 hover:bg-indigo-500",
    badge: "Popular",
    badgeClass: "border-indigo-400/30 bg-indigo-500/12 text-indigo-100",
  },
  2: {
    wrap: "border-cyan-700/70 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.22),_transparent_72%)]",
    icon: "border-cyan-400/30 bg-cyan-500/12",
    bonus: "text-cyan-300",
    button: "bg-cyan-600 border-cyan-800 hover:bg-cyan-500",
    badge: "Value",
    badgeClass: "border-cyan-400/30 bg-cyan-500/12 text-cyan-100",
  },
  3: {
    wrap: "border-violet-700/70 bg-[radial-gradient(circle_at_top,_rgba(167,139,250,0.22),_transparent_72%)]",
    icon: "border-violet-400/30 bg-violet-500/12",
    bonus: "text-violet-300",
    button: "bg-violet-600 border-violet-800 hover:bg-violet-500",
    badge: "Collector",
    badgeClass: "border-violet-400/30 bg-violet-500/12 text-violet-100",
  },
  4: {
    wrap: "border-amber-500/50 bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.26),_transparent_72%)] shadow-[0_18px_40px_-24px_rgba(251,191,36,0.45)]",
    icon: "border-amber-400/35 bg-amber-500/14",
    bonus: "text-amber-300",
    button: "bg-amber-500 border-amber-700 hover:bg-amber-400 text-slate-950",
    badge: "Premium",
    badgeClass: "border-amber-400/35 bg-amber-500/14 text-amber-100",
  },
  5: {
    wrap: "border-fuchsia-500/50 bg-[radial-gradient(circle_at_top,_rgba(217,70,239,0.28),_transparent_72%)] shadow-[0_18px_40px_-24px_rgba(217,70,239,0.45)]",
    icon: "border-fuchsia-400/35 bg-fuchsia-500/14",
    bonus: "text-fuchsia-300",
    button: "bg-[linear-gradient(135deg,rgba(217,70,239,0.95),rgba(99,102,241,0.92))] border-fuchsia-700 hover:brightness-110",
    badge: "Best Value",
    badgeClass: "border-fuchsia-400/35 bg-fuchsia-500/14 text-fuchsia-100",
  },
};

// ---- Cost curves (tweak as needed) ----
const getExtendCost = (extendTime) => {
  const baseCost = 25;                   // first hour cost
  const exponential = (extendTime ?? 12) - 13 + 1; // grows after 13
  return Math.ceil(baseCost * (1.1 ** Math.max(0, exponential)));
};

// simple sample curves—adjust to your real economy later
const getAvatarCost = (size) => {
  const base = 500;                      // example starter cost
  const growth = Math.max(0, (size ?? 1) - 1);
  return Math.ceil(base * (1.4 ** growth));
};

const getBuySizeCost = (max) => {
  const base = 350;
  const growth = Math.max(0, ((max ?? 50) - 50) / 5);
  return Math.ceil(base * (1.1 ** growth));
};

const PLACE_TOKEN_COST = 500;
const PLACE_TOKEN_BUNDLE_SIZE = 5;
const BASE_FREE_DRAW_MAX_PIXELS = 25;
const getFreeDrawMaxPixelCost = (currentMaxPixels) => 500 + (Math.max(0, currentMaxPixels - BASE_FREE_DRAW_MAX_PIXELS) * 250);

const SECTION_THEMES = {
  freeDraw: {
    sectionBorder: "border-cyan-400/20",
    sectionBg: "bg-[linear-gradient(180deg,rgba(14,116,144,0.14),rgba(15,23,42,0.92))]",
    sectionShadow: "shadow-[0_24px_60px_-28px_rgba(34,211,238,0.45)]",
    iconWrap: "border-cyan-300/30 bg-cyan-400/10 text-cyan-100",
    badge: "border-cyan-300/25 bg-cyan-400/10 text-cyan-100",
    value: "text-cyan-200",
    button: "bg-[linear-gradient(135deg,rgba(6,182,212,0.95),rgba(37,99,235,0.92))] border-cyan-900/70 hover:shadow-[0_16px_36px_-18px_rgba(34,211,238,0.75)]",
    glow: "bg-[radial-gradient(circle_at_center,_rgba(34,211,238,0.48)_0%,_transparent_72%)]",
  },
  artwork: {
    sectionBorder: "border-amber-400/20",
    sectionBg: "bg-[linear-gradient(180deg,rgba(180,83,9,0.14),rgba(15,23,42,0.92))]",
    sectionShadow: "shadow-[0_24px_60px_-28px_rgba(251,191,36,0.38)]",
    iconWrap: "border-amber-300/30 bg-amber-400/10 text-amber-100",
    badge: "border-amber-300/25 bg-amber-400/10 text-amber-100",
    value: "text-amber-200",
    button: "bg-[linear-gradient(135deg,rgba(245,158,11,0.95),rgba(234,88,12,0.92))] border-amber-900/70 hover:shadow-[0_16px_36px_-18px_rgba(251,191,36,0.65)]",
    glow: "bg-[radial-gradient(circle_at_center,_rgba(251,191,36,0.42)_0%,_transparent_72%)]",
  },
  profile: {
    sectionBorder: "border-fuchsia-400/20",
    sectionBg: "bg-[linear-gradient(180deg,rgba(168,85,247,0.14),rgba(15,23,42,0.92))]",
    sectionShadow: "shadow-[0_24px_60px_-28px_rgba(217,70,239,0.38)]",
    iconWrap: "border-fuchsia-300/30 bg-fuchsia-400/10 text-fuchsia-100",
    badge: "border-fuchsia-300/25 bg-fuchsia-400/10 text-fuchsia-100",
    value: "text-fuchsia-200",
    button: "bg-[linear-gradient(135deg,rgba(217,70,239,0.94),rgba(99,102,241,0.92))] border-fuchsia-900/70 hover:shadow-[0_16px_36px_-18px_rgba(217,70,239,0.6)]",
    glow: "bg-[radial-gradient(circle_at_center,_rgba(217,70,239,0.42)_0%,_transparent_72%)]",
  },
};

// ---- Small reusable card for actions on Tab 2 ----
function ActionCard({
  icon,
  title,
  subtitle,
  currentLabel = "Current",
  nextLabel = "After +1",
  currentValue,
  nextValue,
  cost,
  ctaText,
  onAction,
  isLoading,
  isDisabled = false,
  error,
  className = "",
  extraContent = null,
  badge = null,
  theme = SECTION_THEMES.freeDraw,
}) {
  return (
    <div
      className={[
        "group relative overflow-hidden rounded-[28px] border p-5 sm:p-6 shadow-md transition-transform duration-200 hover:-translate-y-1",
        "min-h-[320px] bg-slate-900/90",
        theme.sectionBorder,
        theme.sectionShadow,
        className
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={["rounded-2xl border p-3", theme.iconWrap].join(" ")}>
            {icon}
          </div>
          <div className="space-y-2">
            {badge && (
              <div className={["inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.22em]", theme.badge].join(" ")}>
                {badge}
              </div>
            )}
            <div>
              <h3 className="text-xl font-bold text-white sm:text-2xl">{title}</h3>
              {subtitle && <p className="mt-1 max-w-md text-sm leading-6 text-slate-300">{subtitle}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-white/5 bg-slate-950/65 p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{currentLabel}</div>
            <div className="mt-2 text-xl font-bold text-white sm:text-2xl">{currentValue}</div>
            <div className="mt-1 text-xs text-slate-500">Where you are now</div>
          </div>

          <div className="rounded-2xl border border-white/5 bg-slate-950/65 p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{nextLabel}</div>
            <div className={["mt-2 text-xl font-bold sm:text-2xl", theme.value].join(" ")}>{nextValue}</div>
            <div className="mt-1 text-xs text-slate-500">After this upgrade</div>
          </div>
        </div>

        {extraContent}

        <div className="flex items-center justify-between rounded-2xl border border-white/5 bg-slate-950/65 px-4 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Cost</div>
            <div className="mt-1 text-2xl font-bold text-white">{formatInt(cost)}</div>
          </div>
          <div className="text-right text-xs uppercase tracking-[0.18em] text-slate-400">
            Pixel Drops
          </div>
        </div>

        <div className="flex flex-col items-stretch">
          <Button
            onPress={onAction}
            isLoading={isLoading}
            isDisabled={isDisabled}
            className={[
              "w-full rounded-full border-2 px-4 py-2 text-sm font-semibold text-white transition-shadow whitespace-normal text-center sm:text-md",
              "shadow-md shadow-indigo-950/30 hover:shadow-lg",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900",
              theme.button
            ].join(" ")}
          >
            {ctaText}
          </Button>

          {error && <div className="mt-3 text-sm text-rose-300">{error}</div>}
        </div>
      </div>

      <div className={["pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full blur-3xl opacity-30", theme.glow].join(" ")} />
    </div>
  );
}

function ExtensionSection({ icon, title, description, children, theme = SECTION_THEMES.freeDraw }) {
  return (
    <section className={["rounded-3xl border p-5 sm:p-6", theme.sectionBorder, theme.sectionBg, theme.sectionShadow].join(" ")}>
      <div className="flex items-start gap-3">
        <div className={["rounded-2xl border p-2", theme.iconWrap].join(" ")}>
          {icon}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white sm:text-xl">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-400">{description}</p>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
        {children}
      </div>
    </section>
  );
}

export default function StorePage() {
  const backendContext = useContext(BackendContext);

  // Tab state
  const [activeTab, setActiveTab] = useState("buy"); // "buy" | "extend"

  // Buy flow state
  const [selected, setSelected] = React.useState(String(packages[0].id));
  const selectedPkg = packages.find(p => String(p.id) === selected) ?? packages[0];
  const selectedPackTheme = PACK_THEMES[selectedPkg.id] ?? PACK_THEMES[0];
  const [selectedPack, setSelectedPack] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loadingPackId, setLoadingPackId] = useState(null);
  const [elementsReady, setElementsReady] = useState(false);

  // Congrats & auth
  const [showCongrats, setShowCongrats] = useState(false);
  const [awardedTokens, setAwardedTokens] = useState(0);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Not enough drops modal
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientOwned, setInsufficientOwned] = useState(0);
  const [insufficientRequired, setInsufficientRequired] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);

  // Extend Active Time
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState('');

  // Avatar size & Buy size (example fields — wire to your real profile fields)
  const avatarSize = backendContext?.userProfile?.avatarSize ?? 1;
  const buySize     = backendContext?.userProfile?.buySize ?? 50;
  const freeDrawMaxPixels = Number(backendContext?.userProfile?.freeDrawMaxPixels ?? BASE_FREE_DRAW_MAX_PIXELS);
  const placeTokenCount = Number(backendContext?.userProfile?.placeTokens ?? 0);
  const playerDrops = Number(backendContext?.userProfile?.tokens ?? 0);
  const [placeTokenBundleCount, setPlaceTokenBundleCount] = useState(1);
  const placeTokenPurchaseAmount = PLACE_TOKEN_BUNDLE_SIZE * placeTokenBundleCount;
  const placeTokenPurchaseCost = PLACE_TOKEN_COST * placeTokenBundleCount;

  const requireLoginOr = (fn) => {
    if (!backendContext?.userProfile) {
      setLoginModalOpen(true);
      return false;
    }
    return true;
  };

  const ensureBalanceOrOpenModal = (requiredCost) => {
    const owned = Number(backendContext?.userProfile?.tokens ?? 0);
    if (owned < requiredCost) {
      setInsufficientOwned(owned);
      setInsufficientRequired(requiredCost);
      setInsufficientOpen(true);
      return false;
    }
    return true;
  };

  const openUpgradeConfirm = (config, action) => {
    if (!requireLoginOr()) return;
    setConfirmConfig(config);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  const closeUpgradeConfirm = () => {
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmConfig(null);
  };

  const handleConfirmedUpgrade = async () => {
    if (!confirmAction) return;
    setConfirmOpen(false);
    const action = confirmAction;
    setConfirmAction(null);
    setConfirmConfig(null);
    await action();
  };

  // --- Actions: Buy ---
  const handleBuy = async (pack) => {
    if (!requireLoginOr()) return;
    try {
      setLoadingPackId(pack.id);
      setElementsReady(false);
      const response = await backendContext.createStripePaymentIntent(pack.id);
      setClientSecret(response);
      setSelectedPack(pack);
    } finally {
      setLoadingPackId(null);
    }
  };

  // --- Actions: Extend Active Time (+1h) ---
  const handleExtendOneHour = async () => {
    if (!requireLoginOr()) return;
    const nextExtend = backendContext?.userProfile?.extendTime ? (backendContext.userProfile.extendTime + 1) : 13;
    const required = getExtendCost(nextExtend);
    if (!ensureBalanceOrOpenModal(required)) return;

    setExtendError('');
    setIsExtending(true);
    try {
      // Your backend method:
      await backendContext.increaseExtendTime(1);
    } catch (e) {
      setExtendError(e?.message || 'Failed to extend time.');
    } finally {
      setIsExtending(false);
    }
  };

  // --- Actions: Avatar Size +1 ---
  const [isAvatarInc, setIsAvatarInc] = useState(false);
  const [avatarErr, setAvatarErr] = useState('');
  const handleIncreaseAvatar = async () => {
    if (!requireLoginOr()) return;
    const required = getAvatarCost(avatarSize);
    if (!ensureBalanceOrOpenModal(required)) return;

    setAvatarErr('');
    setIsAvatarInc(true);
    try {
      await backendContext.increaseAvatarSize();
    } catch (e) {
      setAvatarErr(e?.message || 'Failed to increase avatar size.');
    } finally {
      setIsAvatarInc(false);
    }
  };

  // --- Actions: Max Buy Size +1 ---
  const [isBuyInc, setIsBuyInc] = useState(false);
  const [buyErr, setBuyErr] = useState('');
  const handleIncreaseBuySize = async () => {
    if (!requireLoginOr()) return;
    const required = getBuySizeCost(buySize);
    if (!ensureBalanceOrOpenModal(required)) return;

    setBuyErr('');
    setIsBuyInc(true);
    try {
      await backendContext.increaseBuySize();
    } catch (e) {
      setBuyErr(e?.message || 'Failed to increase buy size.');
    } finally {
      setIsBuyInc(false);
    }
  };

  const [isFreeDrawCapacityIncreasing, setIsFreeDrawCapacityIncreasing] = useState(false);
  const [freeDrawCapacityErr, setFreeDrawCapacityErr] = useState('');
  const handleIncreaseFreeDrawMaxPixels = async () => {
    if (!requireLoginOr()) return;
    const required = getFreeDrawMaxPixelCost(freeDrawMaxPixels);
    if (!ensureBalanceOrOpenModal(required)) return;

    setFreeDrawCapacityErr('');
    setIsFreeDrawCapacityIncreasing(true);
    try {
      const result = await backendContext.increaseFreeDrawMaxPixels();
      if (result && result !== 'success') {
        setFreeDrawCapacityErr(String(result));
      }
    } catch (e) {
      setFreeDrawCapacityErr(e?.message || 'Failed to increase max free draw pixels.');
    } finally {
      setIsFreeDrawCapacityIncreasing(false);
    }
  };

  const [isBuyingPlaceToken, setIsBuyingPlaceToken] = useState(false);
  const [placeTokenErr, setPlaceTokenErr] = useState('');
  const handlePurchasePlaceToken = async () => {
    if (!requireLoginOr()) return;
    if (!ensureBalanceOrOpenModal(placeTokenPurchaseCost)) return;

    setPlaceTokenErr('');
    setIsBuyingPlaceToken(true);
    try {
      const result = await backendContext.purchasePlaceToken(placeTokenBundleCount);
      if (result && result !== 'success') {
        setPlaceTokenErr(String(result));
      }
    } catch (e) {
      setPlaceTokenErr(e?.message || 'Failed to buy restore tokens.');
    } finally {
      setIsBuyingPlaceToken(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden overflow-y-auto bg-slate-900">
      <AppNavbar activeTab="Get Pixel Drops"/>
      <div className="pt-16 sm:pt-20 pb-20 px-4 max-w-6xl mx-auto">
        <Tabs
          aria-label="Store Tabs"
          selectedKey={activeTab}
          onSelectionChange={(k) => setActiveTab(String(k))}
          variant="underlined"
          classNames={{
            // container
            base: "w-full",
            // the tab list bar
            tabList: [
              "relative flex w-full overflow-hidden rounded-[22px] border border-indigo-400/15 p-1.5",
              "bg-[linear-gradient(135deg,rgba(15,23,42,0.98),rgba(30,41,59,0.92))]",
              "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04),0_14px_32px_-18px_rgba(79,70,229,0.42)]",
              "backdrop-blur-sm"
            ].join(" "),
            // the moving cursor (underline) — make it a soft pill glow
            cursor: [
              "absolute inset-y-1 rounded-2xl",
              "rounded-xl",
              "bg-gradient-to-r from-indigo-600 to-fuchsia-600",
              "shadow-[0_10px_28px_-10px_rgba(129,140,248,0.8)]"
            ].join(" "),
            // each tab button
            tab: [
              "min-h-11 rounded-2xl px-4 text-slate-300 data-[selected=true]:text-white",
              "data-[hover-unselected=true]:text-slate-100 transition-colors"
            ].join(" "),
            // internal content of each tab
            tabContent: "group-data-[selected=true]:text-white font-semibold tracking-[0.01em]",
            // the panel area
            panel: "pt-6",
          }}
        >
          {/* TAB 1: Buy Pixel Drops */}
          <Tab
            key="buy"
            title={<span className="flex items-center gap-2"><Gem className="w-4 h-4"/><span>Buy Pixel Drops</span></span>}
          >
            <div id="packages" className="w-full max-w-5xl mx-auto">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-center">
                <div>
                  <RadioGroup
                    orientation="horizontal"
                    classNames={{
                      wrapper: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3",
                      base: "flex flex-col items-center justify-center",
                    }}
                    value={selected}
                    onValueChange={setSelected}
                  >
                    {packages.map((pkg) => (
                      <CustomRadio key={pkg.id} value={String(pkg.id)}>
                        <div className={["relative mx-auto flex h-full min-h-[272px] w-[230px] max-w-[230px] flex-col items-center justify-between overflow-hidden rounded-2xl border-2 px-5 pb-4 pt-5 transition-transform duration-200 hover:-translate-y-1 group-data-[selected=true]:scale-[1.01] group-data-[selected=true]:shadow-[0_0_0_2px_rgba(255,255,255,0.14)]", PACK_THEMES[pkg.id].wrap].join(" ")}>
                          <div className={["absolute right-0 top-0 rounded-bl-xl border border-t-0 border-r-0 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.2em]", PACK_THEMES[pkg.id].badgeClass].join(" ")}>
                            {PACK_THEMES[pkg.id].badge}
                          </div>
                          <div className="flex flex-col items-center">
                            <div className={["rounded-xl border p-2.5", PACK_THEMES[pkg.id].icon].join(" ")}>
                              <ChromaIcon size={46} variant="purple" />
                            </div>
                            <div className="mt-3 text-center">
                              <h2 className="text-lg font-bold text-white leading-tight">{pkg.title}</h2>
                              <div className="mt-2 text-[2rem] font-bold leading-none text-white">{formatInt(pkg.tokens)}</div>
                              <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-slate-400">Pixel Drops</div>
                              <p className={["mt-3 text-sm font-semibold", PACK_THEMES[pkg.id].bonus].join(" ")}>
                                + {formatInt(pkg.extraTokens)} Bonus
                              </p>
                            </div>
                          </div>
                          <Button
                            className={[
                              "mt-4 rounded-full border-2 px-6 py-1 text-base font-semibold transition-shadow",
                              "shadow-md shadow-indigo-950/30 hover:shadow-lg hover:shadow-indigo-950/40",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900",
                              PACK_THEMES[pkg.id].button
                            ].join(" ")}
                          >
                            {pkg.price}
                          </Button>
                        </div>
                      </CustomRadio>
                    ))}
                  </RadioGroup>
                </div>

                <div className="lg:self-center">
                  <div className="text-xs text-gray-400 tracking-wider uppercase mb-2 text-center lg:text-left">
                    Total Amount
                  </div>

                  <div className={[
                    "flex flex-col gap-3 rounded-[24px] border px-5 py-4 text-white transition-colors duration-200",
                    "bg-[linear-gradient(135deg,rgba(15,23,42,0.98),rgba(30,41,59,0.92))]",
                    selectedPackTheme.wrap
                  ].join(" ")}>
                    <div className="flex items-center gap-3">
                      <div className={["flex h-10 w-10 items-center justify-center rounded-2xl border", selectedPackTheme.icon].join(" ")}>
                        <svg className={["h-5 w-5", selectedPackTheme.bonus].join(" ")} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2L2 12l10 10 10-10L12 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400">Selected Total</div>
                        <div className="mt-1 text-xl font-bold sm:text-2xl">
                          {formatInt(selectedPkg.tokens + selectedPkg.extraTokens)}
                        </div>
                      </div>
                    </div>

                    <div className={["rounded-2xl border px-3 py-2 text-xs sm:text-sm", selectedPackTheme.icon].join(" ")}>
                      <div className="flex items-center gap-1">
                        <svg className={["w-4 h-4", selectedPackTheme.bonus].join(" ")} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2L2 12l10 10 10-10L12 2z" />
                        </svg>
                        {formatInt(selectedPkg.tokens)} +{" "}
                        <span className={["font-semibold", selectedPackTheme.bonus].join(" ")}>
                          {formatInt(selectedPkg.extraTokens)} BONUS
                        </span>
                      </div>
                    </div>

                    <Button
                      className={[
                        "rounded-full border-2 px-6 py-1 text-sm font-semibold transition-shadow",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 sm:text-md",
                        selectedPackTheme.button
                      ].join(" ")}
                      onPress={() => handleBuy(selectedPkg)}
                      isLoading={loadingPackId === selectedPkg.id}
                      isDisabled={loadingPackId !== null}
                    >
                      Buy for {selectedPkg.price}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-3 mb-2 text-center text-[11px] text-gray-400 max-w-md mx-auto leading-relaxed">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c-1.104 0-2 .896-2 2v4h4v-4c0-1.104-.896-2-2-2zM6 11V9a6 6 0 1112 0v2"/>
                  </svg>
                  <span>Secure Payments Powered by <span className="text-indigo-400 font-semibold">Stripe</span></span>
                </div>
                <p>All transactions are processed securely through Stripe. We never store or see your credit card information.</p>
              </div>
            </div>
          </Tab>

          {/* TAB 2: Extensions */}
          <Tab
            key="extend"
            title={<span className="flex items-center gap-2"><Clock className="w-4 h-4"/><span>Extensions</span></span>}
          >
            <div className="w-full max-w-6xl mx-auto">
              <div className="space-y-6">
                <section className="relative overflow-hidden rounded-[32px] border border-cyan-300/25 bg-[linear-gradient(135deg,rgba(14,116,144,0.18),rgba(15,23,42,0.95),rgba(37,99,235,0.18))] p-6 shadow-[0_24px_70px_-30px_rgba(34,211,238,0.52)]">
                  <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[radial-gradient(circle_at_center,_rgba(34,211,238,0.22),_transparent_70%)] blur-3xl" />
                  <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-xl">
                      <div className="inline-flex rounded-full border border-cyan-300/25 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-100">
                        Featured Upgrade
                      </div>
                      <h3 className="mt-4 text-2xl font-bold text-white sm:text-3xl">Max Pixels +1</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-300 sm:text-base">
                        Best for daily free-draw players. Raise your meter cap so you can spend more time creating before you need to wait for refills.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3 text-sm">
                        <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-slate-200">
                          {freeDrawMaxPixels} now
                        </div>
                        <div className="rounded-full border border-cyan-300/25 bg-cyan-400/10 px-3 py-1 text-cyan-100">
                          {freeDrawMaxPixels + 1} after upgrade
                        </div>
                        <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-white">
                          {formatInt(getFreeDrawMaxPixelCost(freeDrawMaxPixels))} Pixel Drops
                        </div>
                      </div>
                    </div>

                    <div className="w-full max-w-sm rounded-[28px] border border-cyan-300/20 bg-slate-950/65 p-5">
                      <div className="flex items-start gap-3">
                        <div className="rounded-2xl border border-cyan-300/25 bg-cyan-400/10 p-3 text-cyan-100">
                          <Zap className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">Recommended for active players</div>
                          <div className="mt-1 text-sm text-slate-400">More available pixels means fewer frustrating pauses.</div>
                        </div>
                      </div>
                      <Button
                        onPress={() => openUpgradeConfirm({
                          title: 'Increase Max Pixels',
                          description: 'Increase your free draw meter capacity by 1 pixel.',
                          cost: getFreeDrawMaxPixelCost(freeDrawMaxPixels),
                          currentBalance: playerDrops,
                          afterBalance: playerDrops - getFreeDrawMaxPixelCost(freeDrawMaxPixels),
                          currentValue: `${freeDrawMaxPixels} pixels`,
                          nextValue: `${freeDrawMaxPixels + 1} pixels`,
                          purchaseLabel: 'Max Pixels +1',
                          confirmLabel: 'Confirm Upgrade',
                        }, handleIncreaseFreeDrawMaxPixels)}
                        isLoading={isFreeDrawCapacityIncreasing}
                        className="mt-5 w-full rounded-full border-2 border-cyan-900/70 bg-[linear-gradient(135deg,rgba(6,182,212,0.95),rgba(37,99,235,0.92))] font-semibold text-white"
                      >
                        Upgrade Capacity
                      </Button>
                      {freeDrawCapacityErr && <div className="mt-3 text-sm text-rose-300">{freeDrawCapacityErr}</div>}
                    </div>
                  </div>
                </section>

                <ExtensionSection
                  icon={<Zap className="h-5 w-5" />}
                  title="Free Draw Mode"
                  description="Build up your free-draw pixel meter and buy refills when you want to jump back in faster."
                  theme={SECTION_THEMES.freeDraw}
                >
                  <ActionCard
                    className="w-full md:max-w-[420px]"
                    icon={<Zap className="w-6 h-6 text-indigo-300" />}
                    title={<span>Max Pixels <span className="text-indigo-400">+1</span></span>}
                    subtitle="Increase how many free-draw pixels your meter can hold at once."
                    currentLabel="Current Capacity"
                    nextLabel="After +1"
                    currentValue={`${freeDrawMaxPixels} pixels`}
                    nextValue={`${freeDrawMaxPixels + 1} pixels`}
                    cost={getFreeDrawMaxPixelCost(freeDrawMaxPixels)}
                    ctaText="Increase Capacity"
                    onAction={() => openUpgradeConfirm({
                      title: 'Increase Max Pixels',
                      description: 'Increase your free draw meter capacity by 1 pixel.',
                      cost: getFreeDrawMaxPixelCost(freeDrawMaxPixels),
                      currentBalance: playerDrops,
                      afterBalance: playerDrops - getFreeDrawMaxPixelCost(freeDrawMaxPixels),
                      currentValue: `${freeDrawMaxPixels} pixels`,
                      nextValue: `${freeDrawMaxPixels + 1} pixels`,
                      purchaseLabel: 'Max Pixels +1',
                      confirmLabel: 'Confirm Upgrade',
                    }, handleIncreaseFreeDrawMaxPixels)}
                    isLoading={isFreeDrawCapacityIncreasing}
                    error={freeDrawCapacityErr}
                    badge="Best for daily players"
                    theme={SECTION_THEMES.freeDraw}
                  />

                  <ActionCard
                    className="w-full"
                    icon={<Zap className="w-6 h-6 text-indigo-300" />}
                    title={<span>Restore Tokens <span className="text-indigo-400">+{placeTokenPurchaseAmount}</span></span>}
                    subtitle="Refill missing pixels instantly when you want to keep drawing without waiting for the meter."
                    currentLabel="Bundle quantity"
                    nextLabel="After purchase"
                    currentValue={
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-white">{placeTokenBundleCount}x bundle{placeTokenBundleCount > 1 ? 's' : ''}</div>
                          <div className="text-xs text-slate-400">{PLACE_TOKEN_BUNDLE_SIZE} Restore Tokens per bundle</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            className="bg-slate-800 text-white"
                            isDisabled={placeTokenBundleCount <= 1 || isBuyingPlaceToken}
                            onPress={() => setPlaceTokenBundleCount((count) => Math.max(1, count - 1))}
                          >
                            -
                          </Button>
                          <div className="min-w-[3rem] text-center text-sm font-semibold text-white">
                            {placeTokenBundleCount}
                          </div>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="flat"
                            className="bg-slate-800 text-white"
                            isDisabled={placeTokenBundleCount >= 20 || isBuyingPlaceToken}
                            onPress={() => setPlaceTokenBundleCount((count) => Math.min(20, count + 1))}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    }
                    nextValue={placeTokenCount + placeTokenPurchaseAmount}
                    cost={placeTokenPurchaseCost}
                    ctaText={`Get +${placeTokenPurchaseAmount}`}
                    onAction={() => openUpgradeConfirm({
                      title: 'Buy Restore Tokens',
                      description: `Buy ${placeTokenPurchaseAmount} Restore Tokens in ${placeTokenBundleCount} bundle${placeTokenBundleCount > 1 ? 's' : ''}.`,
                      cost: placeTokenPurchaseCost,
                      currentBalance: playerDrops,
                      afterBalance: playerDrops - placeTokenPurchaseCost,
                      currentValue: `${placeTokenCount} owned`,
                      nextValue: `${placeTokenCount + placeTokenPurchaseAmount} owned`,
                      purchaseLabel: `Restore Tokens +${placeTokenPurchaseAmount}`,
                      confirmLabel: 'Confirm Purchase',
                    }, handlePurchasePlaceToken)}
                    isLoading={isBuyingPlaceToken}
                    error={placeTokenErr}
                    badge="Best for instant refills"
                    theme={SECTION_THEMES.freeDraw}
                  />
                </ExtensionSection>

                <ExtensionSection
                  icon={<ShoppingCart className="h-5 w-5" />}
                  title="Artwork Mode"
                  description="Upgrades for players who want more room and more time to build inside artwork canvas."
                  theme={SECTION_THEMES.artwork}
                >
                  <ActionCard
                    className="w-full"
                    icon={<Clock className="w-6 h-6 text-indigo-300" />}
                    title={<span>Extend Active Time <span className="text-indigo-400">+1</span></span>}
                    subtitle="Keep your activity streak or benefits going longer."
                    currentLabel="Current Extend Time"
                    nextLabel="After +1 hour"
                    currentValue={backendContext?.userProfile?.extendTime ?? 12}
                    nextValue={(backendContext?.userProfile?.extendTime ?? 12) + 1}
                    cost={getExtendCost((backendContext?.userProfile?.extendTime ?? 12) + 1)}
                    ctaText="Extend"
                    onAction={() => openUpgradeConfirm({
                      title: 'Extend Active Time',
                      description: 'Add one more hour to your active time extension.',
                      cost: getExtendCost((backendContext?.userProfile?.extendTime ?? 12) + 1),
                      currentBalance: playerDrops,
                      afterBalance: playerDrops - getExtendCost((backendContext?.userProfile?.extendTime ?? 12) + 1),
                      currentValue: `${backendContext?.userProfile?.extendTime ?? 12} hours`,
                      nextValue: `${(backendContext?.userProfile?.extendTime ?? 12) + 1} hours`,
                      purchaseLabel: 'Extend Active Time +1',
                      confirmLabel: 'Confirm Upgrade',
                    }, handleExtendOneHour)}
                    isLoading={isExtending}
                    error={extendError}
                    badge="Best for longer ownership"
                    theme={SECTION_THEMES.artwork}
                  />

                  <ActionCard
                    className="w-full"
                    icon={<ShoppingCart className="w-6 h-6 text-indigo-300" />}
                    title={<span>Increase Max Buy Size <span className="text-indigo-400">+5</span></span>}
                    subtitle="Expand the number of Pixels you can purchase in a single action."
                    currentLabel="Current Max"
                    nextLabel="After +5"
                    currentValue={buySize}
                    nextValue={buySize + 5}
                    cost={getBuySizeCost(buySize)}
                    ctaText="Increase Limit"
                    onAction={() => openUpgradeConfirm({
                      title: 'Increase Max Buy Size',
                      description: 'Increase the maximum artwork pixel buy amount by 5.',
                      cost: getBuySizeCost(buySize),
                      currentBalance: playerDrops,
                      afterBalance: playerDrops - getBuySizeCost(buySize),
                      currentValue: `${buySize} pixels`,
                      nextValue: `${buySize + 5} pixels`,
                      purchaseLabel: 'Max Buy Size +5',
                      confirmLabel: 'Confirm Upgrade',
                    }, handleIncreaseBuySize)}
                    isLoading={isBuyInc}
                    error={buyErr}
                    badge="Best for bigger purchases"
                    theme={SECTION_THEMES.artwork}
                  />
                </ExtensionSection>

                <ExtensionSection
                  icon={<UserPlus className="h-5 w-5" />}
                  title="Profile"
                  description="Cosmetic upgrades that improve how your identity shows up across the app."
                  theme={SECTION_THEMES.profile}
                >
                  <ActionCard
                    className="w-full md:max-w-[420px]"
                    icon={<UserPlus className="w-6 h-6 text-indigo-300" />}
                    title={<span>Increase Avatar Size <span className="text-indigo-400">+1</span></span>}
                    subtitle="Expand your profile canvas for more detailed avatars."
                    currentLabel="Current Size"
                    nextLabel="After +1"
                    currentValue={avatarSize}
                    nextValue={avatarSize + 1}
                    cost={getAvatarCost(avatarSize)}
                    ctaText="Increase Size"
                    onAction={() => openUpgradeConfirm({
                      title: 'Increase Avatar Size',
                      description: 'Expand your avatar canvas by 1 size level.',
                      cost: getAvatarCost(avatarSize),
                      currentBalance: playerDrops,
                      afterBalance: playerDrops - getAvatarCost(avatarSize),
                      currentValue: `Size ${avatarSize}`,
                      nextValue: `Size ${avatarSize + 1}`,
                      purchaseLabel: 'Avatar Size +1',
                      confirmLabel: 'Confirm Upgrade',
                    }, handleIncreaseAvatar)}
                    isLoading={isAvatarInc}
                    error={avatarErr}
                    badge="Cosmetic upgrade"
                    theme={SECTION_THEMES.profile}
                  />
                </ExtensionSection>
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* Modal Checkout Form (unchanged) */}
      {clientSecret && selectedPack && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center">
          <div className="bg-slate-900 text-white p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl relative">
            <h2 className="text-xl font-bold mb-4">Complete your purchase</h2>

            <div className="relative">
              {!elementsReady && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md
                                bg-slate-900/70 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2 text-sm text-gray-300">
                    <div className="w-5 h-5 animate-spin rounded-full border border-white/20 border-t-white" />
                    <span>Preparing secure checkout…</span>
                  </div>
                </div>
              )}

              <Elements 
                stripe={stripePromise} 
                options={{ clientSecret, appearance }}
              >
                <CheckoutForm 
                  pack={selectedPack} 
                  onClose={() => {
                    setClientSecret('');
                    setSelectedPack(null);
                  }}
                  onReady={() => setElementsReady(true)}
                  onSuccess={() => {
                    const total = (selectedPack.tokens ?? 0) + (selectedPack.extraTokens ?? 0);
                    setClientSecret('');
                    setSelectedPack(null);
                    setAwardedTokens(total);
                    setShowCongrats(true);
                  }}
                />
              </Elements>
            </div>

            <button
              onClick={() => {
                setClientSecret("");
                setSelectedPack(null);
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-300"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Congrats */}
      <CongratsModal
        isOpen={showCongrats}
        tokens={awardedTokens}
        onClose={() => setShowCongrats(false)}
      />

      {/* Not enough Drops */}
      <InsufficientDropsModal
        isOpen={insufficientOpen}
        owned={insufficientOwned}
        required={insufficientRequired}
        onClose={() => setInsufficientOpen(false)}
        onGoToStore={() => {
          setInsufficientOpen(false);
          const el = document.getElementById('packages');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveTab("buy");
        }}
      />

      {/* Login required */}
      <LogInRequiredModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        message="You must be logged to perform this action."
      />

      <Modal isOpen={confirmOpen} onClose={closeUpgradeConfirm} size="md" backdrop="blur" placement="center">
        <ModalContent className="bg-slate-900 text-white">
          <ModalBody>
            <div className="space-y-4">
              <div>
                <div className="text-xl font-semibold text-white">{confirmConfig?.title}</div>
                <p className="mt-1 text-sm text-slate-300">{confirmConfig?.description}</p>
              </div>

              <div className="space-y-3">
                <div className="rounded-xl border border-white/10 bg-slate-950/70 p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Upgrade</div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white">
                      {confirmConfig?.currentValue}
                    </div>
                    <div className="text-indigo-300 text-lg font-bold">→</div>
                    <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-3 py-2 text-sm font-semibold text-white">
                      {confirmConfig?.nextValue}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-slate-950/70 p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Pixel Drops</div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white">
                      {formatInt(confirmConfig?.currentBalance)}
                    </div>
                    <div className="text-indigo-300 text-lg font-bold">→</div>
                    <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-3 py-2 text-sm font-semibold text-white">
                      {formatInt(confirmConfig?.afterBalance)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={closeUpgradeConfirm}>Cancel</Button>
            <Button className="bg-indigo-600 border-2 border-indigo-800 rounded-full" onPress={handleConfirmedUpgrade}>
              {confirmConfig?.confirmLabel ?? 'Confirm'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

// --- Modal for insufficient funds (unchanged) ---
const InsufficientDropsModal = ({ isOpen, owned, required, onClose, onGoToStore }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="md" backdrop="blur" placement="center">
    <ModalContent className="bg-slate-900 text-white">
      <ModalHeader className="text-lg font-bold">Not enough Pixel Drops</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-300">
          You don’t have enough Pixel Drops to complete this action.
        </p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <div className="bg-[#12172A] rounded-lg p-3 border border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Owned</div>
            <div className="text-base font-semibold">{formatInt(owned)}</div>
          </div>
          <div className="bg-[#12172A] rounded-lg p-3 border border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Required</div>
            <div className="text-base font-semibold text-indigo-300">{formatInt(required)}</div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="light" onPress={onClose}>Cancel</Button>
        <Button className="bg-indigo-600 border-2 border-indigo-800 rounded-full" onPress={onGoToStore}>
          Get more Pixel Drops
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);


